import * as amplitude from '@amplitude/unified'
import $console from '@zuto/utilities/src/$console'

const PAGE_VIEWED_EVENT_TYPE = '[Amplitude] Page Viewed'
const APPLICATION_CATEGORY = 'AppForm'

export enum AmplitudeEvents {
  ApplicationQuestionViewed = 'Application Question Viewed',
  ApplicationStarted = 'Application Started',
  ApplicationSubmitted = 'Application Submitted'
}

export type AmplitudeTrafficContext = {
  isOfficeTraffic: boolean
  isBotTraffic: boolean
  zutoExperiment?: string
}

let trafficContext: AmplitudeTrafficContext = {
  isOfficeTraffic: false,
  isBotTraffic: false
}

/**
 * Sets the traffic/experiment context read by the enrichment plugin registered in
 * {@link registerAmplitudeEnrichmentPlugin}. Call once traffic/experiment values are known
 * (e.g. on Amplitude init), since they aren't otherwise available to a browser-side plugin.
 */
export const setAmplitudeTrafficContext = (context: AmplitudeTrafficContext): void => {
  trafficContext = context
}

/**
 * Derives a page name from the current URL path, e.g. `/apply/car-finance` -> `apply/car-finance`,
 * `/` -> `home`.
 */
const getPageName = (): string => {
  const path = window.location.pathname.replace(/^\/+|\/+$/g, '')
  return path.length > 0 ? path : 'home'
}

/**
 * Registers an Amplitude enrichment plugin that injects app-specific context into outgoing events.
 *
 * Amplitude's default "Page Viewed" event is autocaptured, so app-specific context (traffic type,
 * active experiment, category) can't be passed via a manual track() call - it's injected here
 * instead, right before each event is sent.
 *
 * `is_office_traffic`/`is_bot_traffic`/`zuto_experiment`/`application_category` are added to every
 * event; `page_name` is added only to the Page Viewed event.
 */
export const registerAmplitudeEnrichmentPlugin = (): void => {
  amplitude.add({
    name: 'app-form-context-enrichment',
    type: 'enrichment',
    execute: async (event) => {
      event.event_properties = {
        ...event.event_properties,
        is_office_traffic: trafficContext.isOfficeTraffic,
        is_bot_traffic: trafficContext.isBotTraffic,
        zuto_experiment: trafficContext.zutoExperiment,
        application_category: APPLICATION_CATEGORY,
        ...(event.event_type === PAGE_VIEWED_EVENT_TYPE && {
          page_name: getPageName()
        })
      }

      return event
    }
  })
}

type ApplicationQuestionViewedProperties = {
  question_label: string
}

type ApplicationSubmittedProperties = {
  success: boolean
  is_retry: boolean
}

type AmplitudeEventProperties = ApplicationQuestionViewedProperties | ApplicationSubmittedProperties

/**
 * Tracks a custom Amplitude event. No-ops on the server, and swallows/logs any tracking failure so
 * analytics can never break the calling flow.
 */
export const trackAmplitudeEvent = (eventName: AmplitudeEvents, eventProperties?: AmplitudeEventProperties): void => {
  if (typeof window === 'undefined') return

  const rawGroups = (window as unknown as { OnetrustActiveGroups?: unknown }).OnetrustActiveGroups
  const groups = Array.isArray(rawGroups) ? rawGroups : typeof rawGroups === 'string' ? rawGroups.split(',') : []
  if (!groups.map((g) => String(g).trim()).filter(Boolean).includes('C0002')) return

  try {
    amplitude.track(eventName, eventProperties)
  } catch (err) {
    $console.error(`Amplitude track failed: ${eventName}`, err)
  }
}
